<template>
  <v-empty-state
    class="d-flex flex-column w-100"
    :text="props.message"
  >
    <template #media>
      <div class="d-flex flex-row justify-center align-content-center mb-4">
        <div class="number">
          4
        </div>
        <div class="illustration">
          <div class="circle" />
          <div class="clip">
            <div class="paper">
              <div class="face">
                <div class="eyes">
                  <div class="eye eye-left" />
                  <div class="eye eye-right" />
                </div>
                <div class="rosyCheeks rosyCheeks-left" />
                <div class="rosyCheeks rosyCheeks-right" />
                <div class="mouth" />
              </div>
            </div>
          </div>
        </div>
        <div class="number">
          {{ props.code }}
        </div>
      </div>
    </template>
    <template #actions>
      <v-btn
        class="text-none mt-3"
        prepend-icon="mdi-step-backward-2"
        variant="tonal"
        elevation="8"
        rounded="sm"
        @click.stop="goTo"
      >
        Go to <span class="font-weight-black text-high-emphasis ml-1">{{ isAuthenticated && (props.code != 3 || !props.forceLogout)? 'Homepage' : 'Login' }}</span>
      </v-btn>
    </template>
  </v-empty-state>
</template>

<script setup>
import { storeToRefs } from 'pinia'

import { useAuthentication } from '@/stores/auth'
import { useLayout } from '@/stores/layout'

const authenticationStore = useAuthentication()
const layoutStore = useLayout()

const { isAuthenticated } = storeToRefs(authenticationStore)

const { getHomePage } = storeToRefs(layoutStore)
const { setPage } = layoutStore

const props = defineProps({
  message: {
    default: "Oops. The page you're looking for doesn't exist.",
    type: String
  },
  code: {
    default: 4,
    type: Number
  },
  forceLogout:  {
    default: true,
    type: Boolean
  }
})

const goTo = () => {
  if (authenticationStore.isAuthenticated) {
    if (props.code == 3 && props.forceLogout) authenticationStore.logout()
    else {
      if (authenticationStore.router.currentRoute.value.path == '/') setPage(getHomePage.value)
      else authenticationStore.router.push('/')
    }

  } else authenticationStore.router.push('/welcome')
}
</script>


<style lang="scss" scoped>
/* colors */
$blue: #293b49;
$pink: #fdabaf;
$white: white;

.number {
  font-weight: 900;
  font-size: 15rem;
  line-height: 1;
}

.illustration {
  position: relative;
  width: 12.2rem;
  margin: 0 2.1rem;
}

.circle {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 12.2rem;
  height: 11.4rem;
  border-radius: 50%;
  background-color: $blue;
}

.clip {
  position: absolute;
  bottom: 0.3rem;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  width: 12.5rem;
  height: 13rem;
  border-radius: 0 0 50% 50%;
}

.paper {
  position: absolute;
  bottom: -0.3rem;
  left: 50%;
  transform: translateX(-50%);
  width: 9.2rem;
  height: 12.4rem;
  border: 0.3rem solid $blue;
  background-color: $white;
  border-radius: 0.8rem;

  &::before {
    content: '';
    position: absolute;
    top: -0.7rem;
    right: -0.7rem;
    width: 1.4rem;
    height: 1rem;
    background-color: $white;
    border-bottom: 0.3rem solid $blue;
    transform: rotate(45deg);
  }
}

.face {
  position: relative;
  margin-top: 2.3rem;
}

.eyes {
  position: absolute;
  top: 0;
  left: 2.4rem;
  width: 4.6rem;
  height: 0.8rem;
}

.eye {
  position: absolute;
  bottom: 0;
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
  background-color: $blue;
  animation-name: eye;
  animation-duration: 4s;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;

  &-left {
    left: 0;
  }

  &-right {
    right: 0;
  }
}

.rosyCheeks {
  position: absolute;
  top: 1.6rem;
  width: 1rem;
  height: 0.2rem;
  border-radius: 50%;
  background-color: $pink;

  &-left {
    left: 1.4rem;
  }

  &-right {
    right: 1.4rem;
  }
}

.mouth {
  position: absolute;
  top: 3.1rem;
  left: 50%;
  width: 1.6rem;
  height: 0.2rem;
  border-radius: 0.1rem;
  transform: translateX(-50%);
  background-color: $blue;
}
</style>
